import { createSlice } from "@reduxjs/toolkit";
import { getMinimumLegalAgeDate } from "../../services/functions";

import { getUserInfo } from "../actions/userActions";

const INVISIBLE_CHARS_RE = /[\u3164\u2800\u200B-\u200D\u2060\uFEFF]/g; // Hangul filler, braille blank, ZW*, word-joiner, BOM

const cleanString = (v) => {
  if (v == null) return "";
  return String(v).replace(INVISIBLE_CHARS_RE, "").trim();
};

const isBlank = (v) =>
  v == null ||
  (typeof v === "string" && cleanString(v) === "") ||
  (Array.isArray(v) && v.length === 0);

const normalizeBirthDate = (v) => {
  const s = cleanString(v);
  if (!s) return "";
  const m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s; // fallback as-is if unknown format
  return `${m[1]}-${m[2]}-${m[3]}`; // ✅ keep only YYYY-MM-DD
};
const pick = (...vals) => vals.find((x) => !isBlank(x));

const pickString = (...vals) => {
  const v = pick(...vals);
  return isBlank(v) ? "" : cleanString(v);
};

const applyProfileToInitialConfig = (state, user, subscriber) => {
  state.extras = state.extras || {};

  // subscriber.extras can be [] in your payload -> normalize it to {}
  const subExtras = Array.isArray(subscriber?.extras)
    ? {}
    : subscriber?.extras || {};

  const setIfBlankStr = (key, ...vals) => {
    if (!isBlank(state[key])) return;
    const v = pickString(...vals);
    if (v) state[key] = v;
  };

  const setIfBlankVal = (key, ...vals) => {
    if (
      state[key] != null &&
      !(typeof state[key] === "string" && isBlank(state[key]))
    )
      return;
    const v = pick(...vals);
    if (!isBlank(v)) state[key] = v;
  };

  // basic identity (avoid writing "ㅤ")
  setIfBlankStr("name", user?.name, subscriber?.name);
  setIfBlankStr("surname", user?.surname, subscriber?.surname);

  // contact
  setIfBlankStr("email", user?.email, subscriber?.email);
  setIfBlankStr("telephone", user?.telephone, subscriber?.telephone);

  // subscriber physical
  setIfBlankStr("sex", subscriber?.sex);
  setIfBlankVal("initial_weight", subscriber?.initial_weight);
  // "current_weight" is not a column on the subscriber — the backend
  // computes it from the latest measure-diary entry and only returns
  // it on the home/progress endpoint. For the onboarding form we seed
  // it from initial_weight so existing users see a sane default; on
  // submit, if the user leaves Peso di partenza blank we mirror it
  // back from current_weight.
  setIfBlankVal("current_weight", subscriber?.initial_weight);
  setIfBlankVal("height", subscriber?.height);

  if (isBlank(state.birth_date)) {
    const bd = normalizeBirthDate(pick(subscriber?.birth_date));
    if (bd) state.birth_date = bd;
  }

  // public profile fields
  if (isBlank(state.nickname)) {
    const a = pickString(subscriber?.nickname, subscriber?.nickname);
    if (a) state.nickname = a;
  }

  if (isBlank(state.nickname)) {
    const n = pickString(subscriber?.nickname, subscriber?.nickname);
    if (n) state.nickname = n;
  }

  if (state.country_id == null) {
    const v = pick(subscriber?.country_id);
    if (!isBlank(v)) state.country_id = v;
  }
  setIfBlankStr(
    "country_text",
    subscriber?.country_text,
    subscriber?.country?.name
  );

  if (state.province_id == null) {
    const v = pick(subscriber?.province_id);
    if (!isBlank(v)) state.province_id = v;
  }
  setIfBlankStr(
    "province_text",
    subscriber?.province_text,
    subscriber?.province?.name
  );

  // extras.hobbies
  const hobbies = pick(subExtras?.hobbies, subExtras?.interests);
  if (
    isBlank(state.extras.hobbies) &&
    Array.isArray(hobbies) &&
    hobbies.length
  ) {
    state.extras.hobbies = hobbies;
  }

  // extras.goals
  const goalsRaw = pick(
    subExtras?.goals,
    subscriber?.nutritional_goals,
    subscriber?.goals
  );
  if (
    isBlank(state.extras.goals) &&
    Array.isArray(goalsRaw) &&
    goalsRaw.length
  ) {
    state.extras.goals = goalsRaw
      .map((g) => Number(g?.id ?? g))
      .filter((n) => Number.isFinite(n));
  }

  // extras.goals_draft (UI draft) - only seed it if empty
  if (isBlank(state.extras.goals_draft) && Array.isArray(state.extras.goals)) {
    state.extras.goals_draft = [...state.extras.goals];
  }

  // diet step fields (keep as-is but safe)
  if (state.diet_id == null) {
    const v = pick(subscriber?.diet_id, subscriber?.diet?.id);
    if (!isBlank(v)) state.diet_id = v;
  }
  if (state.keep_weight == null)
    state.keep_weight = pick(subscriber?.keep_weight) ?? state.keep_weight;
  if (state.gluten_free == null)
    state.gluten_free = pick(subscriber?.gluten_free) ?? state.gluten_free;
  if (state.vegetarian == null)
    state.vegetarian = pick(subscriber?.vegetarian) ?? state.vegetarian;
  if (state.is_smart == null)
    state.is_smart = pick(subscriber?.is_smart) ?? state.is_smart;
};

const initialState = {
  birth_date: null,
  freeOrPaid: "free",
  initial_weight: null,
  current_weight: null,
  city_text: "",
  keep_weight: null,
  gluten_free: null,
  country_id: 1,
  country_text: "Italia",
  province_id: null,
  vegetarian: undefined,
  diet_id: null,
  nickname: null,
  days_as_subscriber: 0,
  subscription_days_count: 0,
  card_last4: null,
  badge: {},
  city_id: null,
  telephone: "",
  surname: "",
  avatar: "",
  avatarFile: null,
  image: "",
  height: null,
  name: "",
  sex: "",
  simulate_free: "",
  is_smart: null,
  extras: {
    physical_impediments: [],
    weight_goal: null,
    eating_out: "",
    motivation: "",
    intestine: "",
    cooking: "",
    hobbies: [],
    sleep: "",
    goals: [],
    goals_draft: [],
  },
  nutritionalGoalProtocols: [],
  nutritionalGoalsList: [],
  hydratedFromProfile: false,
};

const initialConfigSlice = createSlice({
  name: "initialConfig",
  initialState,
  reducers: {
    setInitialConfigProperty: (state, action) => {
      const { key } = action.payload;
      let { value } = action.payload;

      // ✅ prevent "empty spaces" from ever entering the store
      if (typeof value === "string" && value.trim() === "") value = "";

      state[key] = value;
    },
    setInitialConfigExtraProperty: (state, action) => {
      const { key } = action.payload;
      let { value } = action.payload;

      if (typeof value === "string" && value.trim() === "") value = "";

      state.extras[key] = value;
    },
    hydrateInitialConfigFromProfile: (state, action) => {
      const { user, subscriber } = action.payload || {};
      applyProfileToInitialConfig(state, user, subscriber);
      state.hydratedFromProfile = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUserInfo.fulfilled, (state, action) => {
      applyProfileToInitialConfig(
        state,
        action.payload?.user,
        action.payload?.master_club_subscriber
      );
      state.hydratedFromProfile = true;
    });
  },
});

export const {
  setInitialConfigProperty,
  setInitialConfigExtraProperty,
  hydrateInitialConfigFromProfile,
} = initialConfigSlice.actions;
export default initialConfigSlice.reducer;
